import {
    AuthContext,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    ITableColumn,
    ITableFetchOptions,
    ProviderCollectionInvoiceDraft,
    renderDate,
    Tooltip,
} from '@enter/core';
import { mdiCash, mdiCheck, mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, parseSortBy, RootStore } from 'stores';
import { draftMessageKind } from 'stores/collection-invoice-draft-message';
import {
    actionColumnSize,
    amountColumnSize,
    BalanceDueCell,
    PaidAmountCell,
    PatientNameCell,
    TableEmpty,
    amountNumber,
    renderCollectionAmount,
} from './patient-collections-cells';
import {
    collectionsSearchEmptyMessage,
    initialFiltersFromPatientIdSearch,
    syncPatientIdSearch,
} from './patient-collections-filters';
import * as S from './patient-collections.styles';

interface Props extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    onResolve: (draftId: string) => void;
    onConfirmBalance: (draftId: string) => void | Promise<void>;
    confirmingId?: string;
    patientName: (first?: string, last?: string) => string;
    patientIdSearch?: string;
}

interface State {
    loading: boolean;
}

const needsRowAction = (
    draft: ProviderCollectionInvoiceDraft,
): { icon: string; label: string } => {
    if ((draft.validationMessages || []).some(m => draftMessageKind(m) === 'payments')) {
        return { icon: mdiCash, label: 'Apply Payments' };
    }
    return { icon: mdiTune, label: 'Set Balance' };
};

const filterSchemas = [];

class PatientCollectionsNeedsPage extends React.Component<Props, State> {
    private presenter: DataTablePresenter<ProviderCollectionInvoiceDraft>;
    private columns: ITableColumn<ProviderCollectionInvoiceDraft>[] = [];

    constructor(props: Props) {
        super(props);
        this.state = { loading: false };
        const rootStore: RootStore = props.rootStore;

        this.columns = [
            {
                id: 'Patient',
                Header: 'Patient Name',
                accessor: row => row,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const d = row.original;
                    const id = d.collectionInvoiceDraftId;
                    return (
                        <PatientNameCell
                            name={props.patientName(d.patientFirstName, d.patientLastName)}
                            sub={[
                                d.externalPatientId,
                                d.patientDateOfBirth ? `DOB ${renderDate(d.patientDateOfBirth)}` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                            onOpen={() => {
                                if (id) props.onResolve(id);
                            }}
                        />
                    );
                },
            },
            {
                id: 'Dos',
                Header: 'DOS',
                accessor: row => row.invoiceDraftItems?.[0]?.serviceDate,
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{renderDate(cell.value) || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Payer',
                Header: 'Payer',
                accessor: 'primaryPayerName',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{cell.value || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'PatientAmt',
                Header: 'Patient',
                accessor: 'invoiceAmount',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => <S.Cell>{renderCollectionAmount(cell.value)}</S.Cell>,
            },
            {
                id: 'Discount',
                Header: 'Discount',
                accessor: 'patientDiscounts',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => {
                    const n = amountNumber(cell.value);
                    return <S.Cell>{n > 0 ? `−${renderCollectionAmount(cell.value)}` : '—'}</S.Cell>;
                },
            },
            {
                id: 'Paid',
                Header: 'Paid',
                accessor: row => {
                    const backing = row.payments || [];
                    if (backing.length > 0) {
                        const n = backing.reduce((t, p) => t + amountNumber(p.paymentAmount), 0);
                        return {
                            amount: n.toFixed(2),
                            currency: backing[0].paymentAmount?.currency ?? row.patientPayments?.currency,
                        };
                    }
                    return row.patientPayments;
                },
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => <PaidAmountCell amount={cell.value} />,
            },
            {
                id: 'Balance',
                Header: 'Balance Due',
                accessor: 'currentBalance',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => <BalanceDueCell amount={cell.value} />,
            },
            {
                id: 'Action',
                Header: 'Action',
                disableSortBy: true,
                ...actionColumnSize,
                Cell: ({ row }) => {
                    const action = needsRowAction(row.original);
                    const id = row.original.collectionInvoiceDraftId;
                    const busy = !!id && props.confirmingId === id;
                    return (
                        <S.ActionCell>
                            <S.RowActionGroup>
                                <Tooltip direction="top" title={<span>Open the invoice and edit the balance</span>}>
                                    <S.RowActionButton
                                        variant="outlined"
                                        disabled={!id || !!props.confirmingId}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (id) props.onResolve(id);
                                        }}>
                                        <Icon path={action.icon} size="16px" />
                                        <span>{action.label}</span>
                                    </S.RowActionButton>
                                </Tooltip>
                                <Tooltip
                                    direction="top"
                                    title={<span>Confirm balance due and move to Ready to Send</span>}>
                                    <S.ConfirmBalanceButton
                                        variant="primary"
                                        disabled={!id || !!props.confirmingId}
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (id) void props.onConfirmBalance(id);
                                        }}>
                                        <Icon path={mdiCheck} size="16px" />
                                        <span>{busy ? 'Saving…' : 'Confirm Balance'}</span>
                                    </S.ConfirmBalanceButton>
                                </Tooltip>
                            </S.RowActionGroup>
                        </S.ActionCell>
                    );
                },
            },
        ];

        this.presenter = new DataTablePresenter<ProviderCollectionInvoiceDraft>({
            name: 'patient-collections-needs',
            data: [],
            columns: this.columns,
            filterSchemas,
            filters: initialFiltersFromPatientIdSearch(props.patientIdSearch),
        });
        void rootStore;
    }

    public componentDidUpdate(prevProps: Props): void {
        syncPatientIdSearch(this.presenter, this.props.patientIdSearch, prevProps.patientIdSearch);
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup?.('patient-collections-needs');
    }

    public render() {
        return (
            <S.TableShell>
                <DataTable
                    presenter={this.presenter}
                    isLoading={this.state.loading}
                    hideFiltering
                    NotFoundComponent={() => (
                        <TableEmpty
                            message={collectionsSearchEmptyMessage(
                                this.props.patientIdSearch,
                                'Nothing needs input. All balances are resolved.',
                            )}
                        />
                    )}
                    onFetch={this.onFetch}
                    onRowClick={row => {
                        const id = row.original.collectionInvoiceDraftId;
                        if (id) this.props.onResolve(id);
                    }}
                />
            </S.TableShell>
        );
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup?.('patient-collections-needs');
            this.props.authContext.setGroup?.('patient-collections-needs');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.props.rootStore.collectionInvoiceStore.loadDraftListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: validFilters,
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
        } catch (err) {
            this.props.dialogContext.showError(err);
        } finally {
            this.setState({ loading: false });
        }
    };
}

export const PatientCollectionsNeeds = inject('rootStore')(
    observer((props: Omit<Props, 'authContext' | 'dialogContext'> & IInjectedRootStore) => {
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return (
            <PatientCollectionsNeedsPage
                {...props}
                authContext={authContext}
                dialogContext={dialogContext}
            />
        );
    }),
);
